/**
 * NAV-ORDER-A — per-browser sidebar order.
 *
 * The order of the "Always" links and of the three sidebar zones is saved in
 * this browser's localStorage only (row ids and zone ids, no content). Every
 * read and write is wrapped in try/catch; a blocked or full localStorage
 * leaves the default order.
 */

export const NAV_ORDER_KEY = "relay_nav_order";

export type NavZoneId = "always" | "projects" | "filters";
export const DEFAULT_ZONE_ORDER: NavZoneId[] = ["always", "projects", "filters"];

export type SavedNavOrder = { version: 1; zones: string[]; always: string[] };

/**
 * Saved ids that still exist (and are visible) in saved order, then the ids
 * the saved list doesn't know, in default order. Anything else in `saved` is
 * dropped. Empty or corrupt input returns `current` unchanged.
 */
export function mergeNavOrder(saved: unknown, current: readonly string[]): string[] {
  if (!Array.isArray(saved) || saved.length === 0) return [...current];
  const allowed = new Set(current);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of saved) {
    if (typeof id === "string" && allowed.has(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  for (const id of current) if (!seen.has(id)) out.push(id);
  return out;
}

/** Moves the item at `from` so it lands before index `to` of the original list (to === length appends). */
export function moveNavItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length || to < 0 || to > list.length) return [...list];
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to > from ? to - 1 : to, 0, item);
  return next;
}

export function loadNavOrder(): SavedNavOrder | null {
  try {
    const raw = localStorage.getItem(NAV_ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return null;
    const strings = (v: unknown): string[] =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    return { version: 1, zones: strings(parsed.zones), always: strings(parsed.always) };
  } catch {
    return null;
  }
}

export function saveNavOrder(order: { zones: string[]; always: string[] }): void {
  try {
    localStorage.setItem(NAV_ORDER_KEY, JSON.stringify({ version: 1, ...order }));
  } catch {
    // blocked or full storage: keep the in-memory order for this session
  }
}

export function clearNavOrder(): void {
  try {
    localStorage.removeItem(NAV_ORDER_KEY);
  } catch {
    // nothing to clear
  }
}
